// This file is automatically generated. Do not edit it directly.
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
// Backwards-compatible: older environments used VITE_SUPABASE_PUBLISHABLE_KEY
// while newer code expects VITE_SUPABASE_ANON_KEY.
const SUPABASE_PUBLISHABLE_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
    },
  },
);

type InvokeArgs = Parameters<typeof supabase.functions.invoke>;
type InvokeResult = Awaited<ReturnType<typeof supabase.functions.invoke>>;
let isForcingAuthReset = false;

async function isAuthFunctionInvokeError(error: unknown): Promise<boolean> {
  if (!error || typeof error !== "object") return false;

  const maybeWithStatus = error as { status?: number };
  if (maybeWithStatus.status === 401 || maybeWithStatus.status === 403) {
    return true;
  }

  const context = (error as { context?: Response | null }).context;
  if (!context) return false;

  if (context.status === 401 || context.status === 403) {
    return true;
  }

  try {
    const payload = (await context.clone().json()) as {
      error?: string;
      message?: string;
    } | null;
    const authText = `${payload?.error ?? ""} ${payload?.message ?? ""}`
      .toLowerCase()
      .trim();
    return (
      authText.includes("authentication") ||
      authText.includes("not authenticated")
    );
  } catch {
    try {
      const text = (await context.clone().text()).toLowerCase();
      return (
        text.includes("authentication") || text.includes("not authenticated")
      );
    } catch {
      return false;
    }
  }
}

function isAuthPayload(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;

  const payload = data as {
    error?: unknown;
    message?: unknown;
    detail?: unknown;
    reason?: unknown;
  };

  const tokens: string[] = [];
  for (const candidate of [
    payload.error,
    payload.message,
    payload.detail,
    payload.reason,
  ]) {
    if (typeof candidate === "string") {
      tokens.push(candidate.toLowerCase());
      continue;
    }

    if (candidate && typeof candidate === "object") {
      const nested = candidate as { code?: unknown; message?: unknown };
      if (typeof nested.code === "string") {
        tokens.push(nested.code.toLowerCase());
      }
      if (typeof nested.message === "string") {
        tokens.push(nested.message.toLowerCase());
      }
    }
  }

  if (tokens.length === 0) return false;

  const combined = tokens.join(" ");
  return (
    combined.includes("not_authenticated") ||
    combined.includes("invalid or expired token") ||
    combined.includes("authentication required") ||
    combined.includes("invalid authentication") ||
    combined.includes("not authenticated") ||
    combined.includes("jwt expired") ||
    combined.includes("token expired")
  );
}

function isDefinitiveRefreshFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const typed = error as {
    status?: number;
    code?: string;
    name?: string;
    message?: string;
    error?: string;
    error_description?: string;
    description?: string;
  };

  if (typed.status === 400 || typed.status === 401 || typed.status === 403) {
    return true;
  }

  const combined = [
    typed.code,
    typed.name,
    typed.message,
    typed.error,
    typed.error_description,
    typed.description,
  ]
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .toLowerCase();

  if (!combined) return false;

  return (
    combined.includes("invalid_grant") ||
    combined.includes("invalid refresh token") ||
    combined.includes("refresh token") ||
    combined.includes("session not found") ||
    combined.includes("session_not_found") ||
    combined.includes("jwt expired") ||
    combined.includes("token expired") ||
    combined.includes("invalid authentication")
  );
}

async function forceSignOutAndRedirectToAuth() {
  if (isForcingAuthReset) return;
  isForcingAuthReset = true;

  try {
    await supabase.auth.signOut({ scope: "local" });
  } catch (signOutError) {
    console.error("Forced local sign-out failed:", signOutError);
  }

  if (typeof window === "undefined") {
    return;
  }

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.location.pathname.startsWith("/auth")) {
    return;
  }

  const authUrl = new URL("/auth", window.location.origin);
  authUrl.searchParams.set("redirect", currentPath);
  window.location.replace(authUrl.toString());
}

const originalInvoke = supabase.functions.invoke.bind(supabase.functions) as (
  ...args: InvokeArgs
) => Promise<InvokeResult>;

supabase.functions.invoke = (async (
  ...args: InvokeArgs
): Promise<InvokeResult> => {
  const firstAttempt = await originalInvoke(...args);

  if (!firstAttempt.error && !isAuthPayload(firstAttempt.data)) {
    return firstAttempt;
  }

  const shouldRetryAuth = firstAttempt.error
    ? await isAuthFunctionInvokeError(firstAttempt.error)
    : isAuthPayload(firstAttempt.data);
  if (!shouldRetryAuth) {
    return firstAttempt;
  }

  const { data: refreshData, error: refreshError } =
    await supabase.auth.refreshSession();
  if (refreshError || !refreshData.session) {
    const definitiveRefreshFailure =
      (!refreshError && !refreshData.session) ||
      isDefinitiveRefreshFailure(refreshError);
    if (definitiveRefreshFailure) {
      await forceSignOutAndRedirectToAuth();
    }
    return firstAttempt;
  }

  return await originalInvoke(...args);
}) as typeof supabase.functions.invoke;
